import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter,
  Route,
  RouteComponentProps,
} from 'react-router-dom';
import {
  connectHits,
} from 'react-instantsearch-dom';
import axios from 'axios';

import { isEqual } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  ALGOLIA_FORM_SUBMISSIONS_INDEX,
  FORM_SUBMISSIONS_ADMIN_PREFIX,
  FORM_SUBMISSIONS_BROWSER_API_PREFIX,
  PROFILES_ADMIN_PREFIX,
  USER_ADMIN_PREFIX,
} from '../../../utils/constants';
import AdminURLSync from '../AdminURLSync';
import useBulkSelected from '../../../utils/hooks/useBulkSelected';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../widgets/ConfirmationModal';
import UserFilterDisplay from '../widgets/filters/display-components/UserFilterDisplay';
import AdminDashboardTableHit from './AdminDashboardTableHit';
import AdminDashboardTableHits from './AdminDashboardTableHits';
import { FormSubmissionInterface, SearchState } from './interfaces';
import AccountsSVG from './assets/AccountsSVG';
import PhoneNumber from './assets/PhoneNumber';

const Hit: React.FC<FormSubmissionInterface> = ({
  id,
  name,
  user_id,
  breeder_name,
  breeder_id,
  phone,
  phone_extn,
  email,
  account_name,
  account_id,
  ip,
  form_type,
  admin_route,
  delete_route,
  selected,
  toggleSelected,
  allowBulkSelect,
  formTypeFromUrl,
}) => {
  const [isHidden, setIsHidden] = useState(false);

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  const updateModal = useCallback((type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: `Would you like to delete this ${form_type.replace(/[^a-z0-9]/gi, ' ')} submission?`,
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(delete_route)
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
        });
    }
  }, [delete_route]);

  // TODO: update `any`
  const values: any = [
    {
      key: 'edit-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          className="edit btn btn-admin-button-gray d-flex align-items-center"
          href={admin_route || '#'}
        >
          <AccountsSVG />
        </a>
      ), // TODO: Update Route
    },
    {
      key: 'delete-mobile',
      wrapperClass: 'd-xl-none',
      value: delete_route && (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete form for: ${name}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
    {
      key: 'name',
      value: user_id
        ? (
          <a
            className="m-0 text-dark-btn-txt font-weight-bold"
            href={`${USER_ADMIN_PREFIX}/${user_id}`}
          >
            {name}
          </a>
        ) : (
          <p className="m-0 text-black font-weight-semi-bold">
            {name}
          </p>
        ),
    },
    {
      key: 'email',
      value: (
        <a
          className="text-table-light-gray m-0"
          href={`mailto:${email}`}
        >
          {email}
        </a>
      ),
    },
    {
      key: 'phone',
      value: phone
        ? (
          <PhoneNumber
            phone={phone}
            phone_extn={phone_extn}
            linkClassName="text-nowrap text-table-light-gray m-0"
            textClassName="text-nowrap text-table-light-gray m-0"
          />
        ) : (
          <></>
        ),
    },
    {
      key: 'account',
      value: account_name
        ? (
          <a
            href={`${USER_ADMIN_PREFIX}/${account_id}`}
            className="text-table-light-gray m-0"
          >
            {account_name}
          </a>
        ) : <></>
      ,
    },
    {
      key: 'ip',
      value: (
        <p className="text-table-light-gray m-0">
          {ip || ''}
        </p>
      ),
    },
    {
      key: 'breeder_name',
      // eslint-disable-next-line no-nested-ternary
      value: breeder_id
        ? <a className="text-black m-0" href={`${PROFILES_ADMIN_PREFIX}/${breeder_id}`}>{breeder_name}</a>
        : breeder_name ? <p className="text-black m-0">{breeder_name}</p> : <></>,
    },
    {
      key: 'form_type',
      value: (
        <p className="text-table-light-gray m-0 text-capitalize">{form_type.replace(/[^a-z0-9]/gi, ' ')}</p>
      ),
    },
    {
      key: 'edit',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          className="edit widescreen mw-100 btn btn-admin-button-gray d-flex align-items-center"
          href={admin_route || '#'}
        >
          <AccountsSVG />
          <span className="ml-1">VIEW</span>
        </a>
      ), // TODO: Update Route
    },
    {
      key: 'delete',
      wrapperClass: 'd-none d-xl-table-cell',
      value: delete_route && (
        <a
          href="#"
          className="widescreen mx-2"
          aria-label={`Delete form for: ${name}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
  ];

  const filteredValues = values.filter((value) => {
    switch (formTypeFromUrl) {
      case 'account_deletion_request':
        return value.key !== 'breeder_name';
      default:
        return true;
    }
  });

  return (
    <>
      {!isHidden && (
        <AdminDashboardTableHit
          values={filteredValues}
          id={id}
          selected={selected}
          toggleSelected={toggleSelected}
          allowBulkSelect={allowBulkSelect}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          show={showModal}
          setShow={setShowModal}
          {...modalData}
        />
      )}
    </>
  );
};

const Hits: React.FC<{
  hits: any,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
  allowBulkSelect: boolean,
  formTypeFromUrl?: string,
}> = ({
  hits,
  selected,
  toggleSelected,
  allowBulkSelect,
  formTypeFromUrl,
}) => {
  return (
    <AdminDashboardTableHits
      hits={hits}
      Hit={Hit}
      selected={selected}
      toggleSelected={toggleSelected}
      allowBulkSelect={allowBulkSelect}
      formTypeFromUrl={formTypeFromUrl}
    />
  );
};

const CustomHits = connectHits(Hits);

const AdminFormSubmissions: React.FC<RouteComponentProps> = ({
  history,
  location,
  match,
}): ReactElement => {
  const getFormType = (browserLocation) => {
    const { search } = browserLocation;
    const query = new URLSearchParams(search);
    return query.get('type') || 'all'; // Default to 'all'
  };

  const loadInitialFormState = (formType) => {
    let facetType = '';

    switch (formType) {
      case 'all':
      case 'reported_ad':
      case 'reported_seller':
      case 'seller_review':
      case 'account_contact_change':
      case 'testimonial':
      case 'listing_change':
      case 'contact':
      case 'account_deletion_request':
        facetType = formType;
        break;
      default:
        facetType = '';
    }
    const facetFilters = (facetType === 'all' || !facetType)
      ? []
      : [`form_type:${facetType}`];
    return {
      configure: {
        numericFilters: [],
        facetFilters,
      },
      query: '',
    };
  };

  const [formType, setFormType] = useState(getFormType(location));
  const [deleteType, setDeleteType] = useState('');
  const [externalSearchState, setExternalSearchState] = useState<SearchState>(
    loadInitialFormState(formType),
  );

  useEffect(() => {
    let newDeleteType = '';

    // eslint-disable-next-line default-case
    switch (formType) {
      case 'reported_ad':
      case 'reported_seller':
        newDeleteType = 'report';
        break;
      case 'seller_review':
        newDeleteType = 'seller-review';
        break;
      case 'account_contact_change':
        newDeleteType = 'account-contact-change-request';
        break;
      case 'testimonial':
        newDeleteType = 'testimonial';
        break;
      case 'listing_change':
        newDeleteType = 'listing-change-request';
        break;
      case 'contact':
        newDeleteType = 'basic-contact';
        break;
      case 'account_deletion_request':
        newDeleteType = 'account-deletion-request';
        break;
    }
    if (newDeleteType !== deleteType) {
      setDeleteType(newDeleteType);
    }
  }, [formType, deleteType]);

  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    toggleAllSelected,
    allSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected();

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No form submissions have been selected. Please, select one or more ads to bulk edit.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
      });
    } else if (!deleteType) {
      setModalData({
        message: 'Form submissions must all be of the same type to perform bulk actions.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'delete':
          setModalData({
            message: `Would you like to delete ${bulkSelected.length} form submissions?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`${FORM_SUBMISSIONS_BROWSER_API_PREFIX}/${deleteType}/${bulkSelected.join(',')}`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, formType, deleteType, reloadPage]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  useEffect(() => {
    const type = getFormType(location);

    if (type !== formType) {
      setFormType(type);
    }
  }, [location]);

  useEffect(() => {
    if (formType === 'all') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, [])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: [],
            },
          });
        }
      }
    } else if (formType === 'reported_ad') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['form_type:reported_ad'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['form_type:reported_ad'],
            },
          });
        }
      }
    } else if (formType === 'reported_seller') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['form_type:reported_seller'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['form_type:reported_seller'],
            },
          });
        }
      }
    } else if (formType === 'seller_review') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['form_type:seller_review'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['form_type:seller_review'],
            },
          });
        }
      }
    } else if (formType === 'account_contact_change') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['form_type:account_contact_change'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['form_type:account_contact_change'],
            },
          });
        }
      }
    } else if (formType === 'testimonial') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['form_type:testimonial'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['form_type:testimonial'],
            },
          });
        }
      }
    } else if (formType === 'listing_change') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['form_type:listing_change'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['form_type:listing_change'],
            },
          });
        }
      }
    } else if (formType === 'contact') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['form_type:contact'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['form_type:contact'],
            },
          });
        }
      }
    } else if (formType === 'account_deletion_request') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['form_type:account_deletion_request'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['form_type:account_deletion_request'],
            },
          });
        }
      }
    }
  }, [formType]);

  const tableHeaderRowValues = [
    {
      key: 'name',
      value: 'Name',
      sort: {
        asc: `${ALGOLIA_FORM_SUBMISSIONS_INDEX}_name_az`,
        desc: `${ALGOLIA_FORM_SUBMISSIONS_INDEX}_name_za`,
      },
    },
    {
      key: 'email',
      value: 'Email',
      sort: {
        asc: `${ALGOLIA_FORM_SUBMISSIONS_INDEX}_email_asc`,
      },
    },
    {
      key: 'phone',
      value: 'Phone',
    },
    {
      key: 'account',
      value: 'Account',
    },
    {
      key: 'ip_address',
      value: 'IP Address',
    },
    {
      key: 'breeder_name',
      value: 'Breeder Name',
    },
    {
      key: 'form',
      value: 'Form',
    },
  ];

  const filteredTableHeaderRowValues = tableHeaderRowValues.filter((value) => {
    switch (formType) {
      case 'account_deletion_request':
        return value.key !== 'breeder_name';
      default:
        return true;
    }
  });

  return (
    <>
      <AdminURLSync
        title={`${formType.replaceAll('_', ' ')} Forms`}
        numEmptyStartHeaders={2}
        numEmptyEndHeaders={2}
        setBulkAction={setBulkAction}
        bulkAction={bulkAction}
        setHits={setHits}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        clearBulkSelected={clearBulkSelected}
        allowBulkSelect={!!deleteType}
        bulkActions={deleteType
          ? [
            {
              label: 'Delete',
              value: 'delete',
            },
          ] : undefined}
        externalQueryParams={[
          {
            key: 'type',
            value: formType,
          },
        ]}
        history={history}
        location={location}
        match={match}
        externalSearchState={externalSearchState}
        tableHeaderRowValues={filteredTableHeaderRowValues}
        indexName={ALGOLIA_FORM_SUBMISSIONS_INDEX!}
        customHits={(
          <CustomHits
            selected={bulkSelected}
            toggleSelected={updateBulkSelected}
            allowBulkSelect={!!deleteType}
            formTypeFromUrl={formType}
          />
        )}
        attributes={[
          {
            type: 'facet',
            attribute: 'breeder_name',
            label: 'Breeder Name',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            DisplayComponent: <UserFilterDisplay title="Breeder Name:" />,
            placeholder: '',
          },
        ]}
        dateAttributes={[]}
        tabs={[
          {
            title: 'All',
            route: `${FORM_SUBMISSIONS_ADMIN_PREFIX}?type=all`,
            key: 'type=all',
            indexName: ALGOLIA_FORM_SUBMISSIONS_INDEX,
            facetFilters: [],
            numericFilters: [],
            showCountInTab: true,
          },
          {
            title: 'Contact',
            route: `${FORM_SUBMISSIONS_ADMIN_PREFIX}?type=contact`,
            key: 'type=contact',
            indexName: ALGOLIA_FORM_SUBMISSIONS_INDEX,
            facetFilters: ['form_type:contact'],
            numericFilters: [],
            showCountInTab: true,
          },
          {
            title: 'Listing Change',
            route: `${FORM_SUBMISSIONS_ADMIN_PREFIX}?type=listing_change`,
            key: 'type=listing_change',
            indexName: ALGOLIA_FORM_SUBMISSIONS_INDEX,
            facetFilters: ['form_type:listing_change'],
            numericFilters: [],
            showCountInTab: true,
          },
          {
            title: 'Account Contact Change',
            route: `${FORM_SUBMISSIONS_ADMIN_PREFIX}?type=account_contact_change`,
            key: 'type=account_contact_change',
            indexName: ALGOLIA_FORM_SUBMISSIONS_INDEX,
            facetFilters: ['form_type:account_contact_change'],
            numericFilters: [],
            showCountInTab: true,
          },
          {
            title: 'Seller Review',
            route: `${FORM_SUBMISSIONS_ADMIN_PREFIX}?type=seller_review`,
            key: 'type=seller_review',
            indexName: ALGOLIA_FORM_SUBMISSIONS_INDEX,
            facetFilters: ['form_type:seller_review'],
            numericFilters: [],
            showCountInTab: true,
          },
          {
            title: 'Testimonial',
            route: `${FORM_SUBMISSIONS_ADMIN_PREFIX}?type=testimonial`,
            key: 'type=testimonial',
            indexName: ALGOLIA_FORM_SUBMISSIONS_INDEX,
            facetFilters: ['form_type:testimonial'],
            numericFilters: [],
            showCountInTab: true,
          },
          {
            title: 'Reported Ads',
            route: `${FORM_SUBMISSIONS_ADMIN_PREFIX}?type=reported_ad`,
            key: 'type=reported_ad',
            indexName: ALGOLIA_FORM_SUBMISSIONS_INDEX,
            facetFilters: ['form_type:reported_ad'],
            numericFilters: [],
            showCountInTab: true,
          },
          {
            title: 'Reported Sellers',
            route: `${FORM_SUBMISSIONS_ADMIN_PREFIX}?type=reported_seller`,
            key: 'type=reported_seller',
            indexName: ALGOLIA_FORM_SUBMISSIONS_INDEX,
            facetFilters: ['form_type:reported_seller'],
            numericFilters: [],
            showCountInTab: true,
          },
          {
            title: 'Account Deletion Requests',
            route: `${FORM_SUBMISSIONS_ADMIN_PREFIX}?type=account_deletion_request`,
            key: 'type=account_deletion_request',
            indexName: ALGOLIA_FORM_SUBMISSIONS_INDEX,
            facetFilters: ['form_type:account_deletion_request'],
            numericFilters: [],
            showCountInTab: true,
          },
        ]}
      />
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        clearBulkSelected={clearBulkSelected}
        {...modalData}
      />
    </>
  );
};

export default AdminFormSubmissions;

if (document.getElementById('react-admin-dashboard-table-form-submissions')) {
  const element = document.getElementById('react-admin-dashboard-table-form-submissions');

  ReactDOM.render(
    <BrowserRouter>
      <Route
        path={FORM_SUBMISSIONS_ADMIN_PREFIX}
        component={AdminFormSubmissions}
      />
    </BrowserRouter>,
    element,
  );
}
